"use client";

import { FC, useEffect, useState } from "react";
import { MdCancel, MdCheckCircle } from "react-icons/md";
import {
  Entry,
  ProgramExplanation,
  QualificationExplanation,
  QualifierExplanation,
  RuleExplanation,
  TaxIncentiveStatuses,
} from "@/prisma-package/src";
import { explainEntryQualificationAction } from "@/app/_lib/actions/ledger.actions";
import { formatMoney } from "@/app/_lib/utils/format-money.utils";
import {
  humanizeActualValue,
  humanizeDocType,
  humanizeQualifier,
} from "@/app/_lib/utils/qualification-explanation.utils";

type ExplanationWithLabels = QualificationExplanation & {
  docTypeLabels: Record<string, string>;
};

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  [TaxIncentiveStatuses.QUALIFIED]: {
    label: "Qualified",
    className: "badge-success",
  },
  [TaxIncentiveStatuses.POTENTIALLY_QUALIFIED]: {
    label: "Potentially qualified",
    className: "badge-info",
  },
  [TaxIncentiveStatuses.PENDING_REVIEW]: {
    label: "Pending review",
    className: "badge-warning",
  },
  [TaxIncentiveStatuses.NOT_QUALIFIED]: {
    label: "Not qualified",
    className: "badge-error",
  },
};

// A matched rule isn't necessarily a qualifying rule — its action decides the
// outcome (e.g. a "Non Australian, Non QAPE" rule matches in order to mark the
// entry NOT_QUALIFIED). Present the match in the outcome's terms.
const RULE_OUTCOMES: Record<
  string,
  { headline: string; progressClass: string }
> = {
  [TaxIncentiveStatuses.QUALIFIED]: {
    headline: "Qualifies under rule:",
    progressClass: "progress-success",
  },
  [TaxIncentiveStatuses.POTENTIALLY_QUALIFIED]: {
    headline: "May qualify under rule:",
    progressClass: "progress-info",
  },
  [TaxIncentiveStatuses.PENDING_REVIEW]: {
    headline: "Flagged for review by rule:",
    progressClass: "progress-warning",
  },
  [TaxIncentiveStatuses.NOT_QUALIFIED]: {
    headline: "Disqualified by rule:",
    progressClass: "progress-error",
  },
};

const isDisqualifying = (program: ProgramExplanation): boolean =>
  program.matchedRule?.appliedStatus === TaxIncentiveStatuses.NOT_QUALIFIED;

const QualifierRow: FC<{
  qualifier: QualifierExplanation;
  docTypeLabels: Record<string, string>;
}> = ({ qualifier, docTypeLabels }) => {
  // A parent row reports its own condition; failing children show themselves.
  const ownMatched = qualifier.selfMatched;

  return (
    <li>
      <div className="flex items-start gap-2">
        {ownMatched ? (
          <MdCheckCircle className="text-success mt-1 shrink-0" />
        ) : (
          <MdCancel className="text-error mt-1 shrink-0" />
        )}
        <div>
          <p>{humanizeQualifier(qualifier)}</p>
          <p className="text-sm opacity-60">
            Entry value: {humanizeActualValue(qualifier.actualValue)}
          </p>
          {qualifier.missingDocs && qualifier.missingDocs.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {qualifier.missingDocs.map((docType) => (
                <span
                  key={docType}
                  className="badge badge-error badge-outline badge-sm"
                >
                  Missing: {humanizeDocType(docType, docTypeLabels)}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {qualifier.children && qualifier.children.length > 0 && (
        <ul className="ml-6 mt-2 flex flex-col gap-2">
          {qualifier.children.map((child) => (
            <QualifierRow
              key={child.id}
              qualifier={child}
              docTypeLabels={docTypeLabels}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const RuleChecklist: FC<{
  rule: RuleExplanation;
  progressClass: string;
  docTypeLabels: Record<string, string>;
}> = ({ rule, progressClass, docTypeLabels }) => (
  <div className="flex flex-col gap-2">
    <p>
      Matched {rule.matchedCount} of {rule.totalCount} criteria
    </p>
    <progress
      className={`progress w-full max-w-md ${progressClass}`}
      value={rule.matchedCount}
      max={rule.totalCount || 1}
    />
    <ul className="flex flex-col gap-3 mt-2">
      {rule.qualifiers.map((qualifier) => (
        <QualifierRow
          key={qualifier.id}
          qualifier={qualifier}
          docTypeLabels={docTypeLabels}
        />
      ))}
    </ul>
  </div>
);

const ProgramSection: FC<{
  program: ProgramExplanation;
  showProgramHeader: boolean;
  docTypeLabels: Record<string, string>;
}> = ({ program, showProgramHeader, docTypeLabels }) => {
  const localityLabel = program.locality
    ? [program.locality.region, program.locality.country]
        .filter(Boolean)
        .join(", ")
    : null;

  return (
    <div className="border border-base-300 rounded-lg p-4 flex flex-col gap-3">
      {showProgramHeader && (
        <div>
          <strong className="text-lg">{program.programName}</strong>
          {localityLabel && (
            <span className="pl-2 text-sm opacity-60">{localityLabel}</span>
          )}
        </div>
      )}

      {program.matchedRule ? (
        <>
          <p>
            {RULE_OUTCOMES[program.matchedRule.appliedStatus ?? ""]?.headline ??
              "Matched rule:"}{" "}
            <strong>{program.matchedRule.ruleName}</strong>
          </p>
          {program.matchedRule.appliedStatus ===
            TaxIncentiveStatuses.NOT_QUALIFIED && (
            <p className="text-sm text-error">
              This rule marks matching entries as not qualified for this
              program.
            </p>
          )}
          <RuleChecklist
            rule={program.matchedRule}
            progressClass={
              RULE_OUTCOMES[program.matchedRule.appliedStatus ?? ""]
                ?.progressClass ?? "progress-success"
            }
            docTypeLabels={docTypeLabels}
          />
          {program.matchedRule.appliedStatus ===
          TaxIncentiveStatuses.NOT_QUALIFIED ? (
            <p className="text-sm opacity-60">
              No incentive from this program — the entry is disqualified by
              this rule.
            </p>
          ) : (
            <p>
              Estimated incentive from this program:{" "}
              <strong>
                {formatMoney(program.contributionCents, {
                  inputFormat: "cents",
                })}
              </strong>
            </p>
          )}
          {(program.matchedRule.appliedFf1 ||
            program.matchedRule.appliedFf2) && (
            <p className="text-sm">
              Codes applied by this rule:
              {program.matchedRule.appliedFf1 && (
                <span className="pl-2">
                  FF1: <strong>{program.matchedRule.appliedFf1}</strong>
                </span>
              )}
              {program.matchedRule.appliedFf2 && (
                <span className="pl-2">
                  FF2: <strong>{program.matchedRule.appliedFf2}</strong>
                </span>
              )}
            </p>
          )}
        </>
      ) : program.unmatchedRules.length > 0 ? (
        <>
          <p>
            No rule matched this entry — it didn&apos;t meet any criterion of
            this program&apos;s{" "}
            {program.unmatchedRules.length === 1
              ? "rule"
              : `${program.unmatchedRules.length} rules`}
            .
          </p>
          <details>
            <summary className="cursor-pointer text-sm link">
              See why
            </summary>
            <div className="flex flex-col gap-4 mt-3">
              {program.unmatchedRules.map((rule) => (
                <div key={rule.ruleId}>
                  <p className="text-sm">
                    <strong>{rule.ruleName}</strong>
                  </p>
                  <ul className="flex flex-col gap-3 mt-2">
                    {rule.qualifiers.map((qualifier) => (
                      <QualifierRow
                        key={qualifier.id}
                        qualifier={qualifier}
                        docTypeLabels={docTypeLabels}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </details>
        </>
      ) : (
        <p className="text-sm opacity-60">
          This program has no rules to evaluate.
        </p>
      )}
    </div>
  );
};

const QualificationExplainer: FC<{
  projectId: string;
  entry: Entry;
}> = ({ projectId, entry }) => {
  const [explanation, setExplanation] = useState<ExplanationWithLabels | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchExplanation = async () => {
      const [actionError, result] = await explainEntryQualificationAction({
        projectId,
        entryId: entry.id,
      });
      if (cancelled) return;
      if (actionError || !result) {
        setError("Could not load the qualification breakdown for this entry.");
        return;
      }
      setExplanation(result as ExplanationWithLabels);
    };

    fetchExplanation();

    return () => {
      cancelled = true;
    };
  }, [projectId, entry.id]);

  const storedStatus =
    entry.taxIncentiveStatus ?? TaxIncentiveStatuses.PENDING_REVIEW;
  const badge =
    STATUS_BADGES[storedStatus] ?? STATUS_BADGES[TaxIncentiveStatuses.PENDING_REVIEW];

  const ruleStatuses =
    explanation?.programs
      .map((program) => program.matchedRule?.appliedStatus)
      .filter((status): status is string => !!status) ?? [];
  const statusDiffersFromRules =
    explanation !== null &&
    ((ruleStatuses.length > 0 && !ruleStatuses.includes(storedStatus)) ||
      (ruleStatuses.length === 0 &&
        storedStatus === TaxIncentiveStatuses.QUALIFIED));

  const neverAnalyzed = !entry.rulesetId && entry.certainty == null;

  return (
    <div>
      <h3 className="flex items-center gap-3 text-xl mb-4">
        Qualification:
        <span className={`badge ${badge.className}`}>{badge.label}</span>
      </h3>

      {statusDiffersFromRules && (
        <p className="text-sm opacity-60 mb-3">
          This status differs from the automated rule result and may have been
          set manually.
        </p>
      )}

      {neverAnalyzed && (
        <p className="text-sm opacity-60 mb-3">
          This entry has not been analyzed yet. The breakdown below is a live
          evaluation against the project&apos;s current rules.
        </p>
      )}

      {error && <p className="text-error">{error}</p>}

      {!error && !explanation && (
        <div className="flex items-center gap-2">
          <span className="loading loading-spinner loading-sm" />
          <span className="opacity-60">Evaluating incentive rules...</span>
        </div>
      )}

      {explanation && !explanation.hasRules && (
        <div className="alert alert-info max-w-xl">
          <span>
            No incentive rules are configured for this project yet, so this
            entry can&apos;t be evaluated.
          </span>
        </div>
      )}

      {explanation && explanation.hasRules && (
        <div className="flex flex-col gap-4 max-w-xl">
          {explanation.programs.map((program, idx) => (
            <ProgramSection
              key={program.programId ?? `program_${idx}`}
              program={program}
              showProgramHeader={
                explanation.programs.length > 1 || !!program.programId
              }
              docTypeLabels={explanation.docTypeLabels}
            />
          ))}
          {explanation.programs.length > 1 && (
            <p>
              Total estimated incentive across programs:{" "}
              <strong className="text-lg">
                {formatMoney(
                  explanation.programs
                    .filter((program) => !isDisqualifying(program))
                    .reduce(
                      (sum, program) => sum + program.contributionCents,
                      0
                    ),
                  { inputFormat: "cents" }
                )}
              </strong>
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default QualificationExplainer;
